import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { CA_COUNTIES as COUNTIES_REF } from '@lds-efsp/shared-contracts';
import { useOrderForm } from '../contexts/OrderFormContext';
import DocumentUploader from '../components/DocumentUploader';
import OrderSummaryPanel from '../components/OrderSummaryPanel';
import PricingEstimate from '../components/PricingEstimate';
import FormInput, { FormSelect, FormCheckbox } from '../../setup/components/FormInput';
import { useNavigate } from 'react-router';

const countyOptions = [
  { value: '', label: 'Select county...' },
  ...COUNTIES_REF.map(c => ({ value: c.name, label: c.name })),
];

interface CourtesyCopyFormProps {
  brand: BrandConfig;
}

const CourtesyCopyForm: React.FC<CourtesyCopyFormProps> = ({ brand }) => {
  const navigate = useNavigate();
  const {
    caseNumber,
    caseTitle,
    priority,
    typeData,
    documents,
    updateCommonFields,
    updateTypeData,
    uploadDocument,
    removeDocument,
    submitOrder,
  } = useOrderForm();

  const data = typeData as { county?: string; state?: string; isFamily?: boolean };

  return (
    <div className="flex gap-6">
      <div className="flex-1 space-y-6">
        {/* Case info */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Case Information
          </h3>
          <FormInput
            brand={brand}
            label="Case Number"
            name="caseNumber"
            value={caseNumber}
            onChange={(e) => updateCommonFields({ caseNumber: e.target.value })}
            required
          />
          <FormInput
            brand={brand}
            label="Case Title"
            name="caseTitle"
            value={caseTitle}
            onChange={(e) => updateCommonFields({ caseTitle: e.target.value })}
            required
          />
        </div>

        {/* Type-specific fields */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Courtesy Copy Details
          </h3>
          <FormSelect
            brand={brand}
            label="County"
            name="county"
            value={data.county ?? ''}
            onChange={(e) => updateTypeData({ county: e.target.value } as Partial<typeof typeData>)}
            options={countyOptions}
            required
          />
          <FormCheckbox
            brand={brand}
            label="Family case"
            name="isFamily"
            checked={data.isFamily ?? false}
            onChange={(e) => updateTypeData({ isFamily: e.target.checked } as Partial<typeof typeData>)}
          />
          <PricingEstimate brand={brand} county={data.county ?? ''} priority={priority} />
        </div>

        {/* Documents */}
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Documents
          </h3>
          <DocumentUploader
            brand={brand}
            documents={documents}
            onUpload={uploadDocument}
            onRemove={removeDocument}
          />
        </div>
      </div>

      {/* Summary sidebar */}
      <div className="w-80 flex-shrink-0 hidden lg:block">
        <OrderSummaryPanel
          brand={brand}
          onSubmit={async () => {
            try {
              await submitOrder();
              navigate('/orders');
            } catch {
              // Error displayed in panel
            }
          }}
        />
      </div>
    </div>
  );
};

export default CourtesyCopyForm;
